import React from "react";
import { Pressable, StyleSheet, View } from "react-native";
import { CheckIcon, XIcon, ArrowCounterClockwiseIcon, ArrowLeftIcon, ArrowRightIcon } from "@phosphor-icons/react";

import CustomText from "../../../../../components/common/general/CustomText/CustomText";
import { colors } from "../../../../../styles/design-system";
import type {
  CourseEvaluatorReviewDecision,
  CourseEvaluatorReviewSidebarItemMock,
} from "../mockData/courseEvaluatorReview.mockData";

interface ReviewActionPanelProps {
  selectedReviewItem: CourseEvaluatorReviewSidebarItemMock | null;
  decision: CourseEvaluatorReviewDecision;
  onAccept?: () => void;
  onReject?: () => void;
  onUndo?: () => void;
  onNavigateNext?: () => void;
  onNavigatePrev?: () => void;
}

function ReviewActionPanel({
  selectedReviewItem,
  decision,
  onAccept,
  onReject,
  onUndo,
  onNavigateNext,
  onNavigatePrev,
}: ReviewActionPanelProps) {
  if (!selectedReviewItem) {
    return null;
  }

  const canUndo = !!onUndo;

  if (decision === "synced") {
    return (
      <View style={styles.bar}>
        <View style={styles.leftGroup}>
          <View style={styles.badgeBrand}>
            <CustomText
              text="Published"
              useTranslationText={false}
              size="xs"
              weight="bold"
              style={styles.badgeBrandText}
            />
          </View>
          <CustomText
            text="Changes have been published."
            useTranslationText={false}
            size="xs"
            style={styles.mutedText}
          />
        </View>
        <NavControls canUndo={canUndo} onUndo={onUndo} onNext={onNavigateNext} onPrev={onNavigatePrev} />
      </View>
    );
  }

  return (
    <View style={styles.bar}>
      <View style={styles.leftGroup}>
        <Pressable
          accessibilityRole="button"
          accessibilityLabel="Add to queue"
          onPress={onAccept}
          style={styles.acceptButton}>
          <CheckIcon size={14} color={colors.white} weight="bold" />
          <CustomText
            text="Add to Queue"
            useTranslationText={false}
            size="xs"
            weight="medium"
            style={styles.acceptButtonText}
          />
        </Pressable>

        <Pressable accessibilityRole="button" accessibilityLabel="Skip" onPress={onReject} style={styles.skipButton}>
          <XIcon size={14} color={colors.grey[700]} weight="bold" />
          <CustomText text="Skip" useTranslationText={false} size="xs" weight="medium" style={styles.skipButtonText} />
        </Pressable>

        {decision && (
          <View style={[styles.badge, decision === "rejected" ? styles.badgeError : styles.badgeSuccess]}>
            <CustomText
              text={decision === "edited" ? "Accepted (edited)" : decision === "accepted" ? "Accepted" : "Rejected"}
              useTranslationText={false}
              size="xs"
              weight="medium"
              style={decision === "rejected" ? styles.badgeErrorText : styles.badgeSuccessText}
            />
          </View>
        )}
      </View>

      <NavControls canUndo={canUndo} onUndo={onUndo} onNext={onNavigateNext} onPrev={onNavigatePrev} />
    </View>
  );
}

interface NavControlsProps {
  canUndo: boolean;
  onUndo?: () => void;
  onNext?: () => void;
  onPrev?: () => void;
}

function NavControls({ canUndo, onUndo, onNext, onPrev }: NavControlsProps) {
  return (
    <View style={styles.rightGroup}>
      <Pressable
        accessibilityRole="button"
        accessibilityLabel="Undo last decision"
        accessibilityState={{ disabled: !canUndo }}
        onPress={onUndo}
        disabled={!canUndo}
        style={[styles.ghostButton, !canUndo && styles.ghostButtonDisabled]}>
        <ArrowCounterClockwiseIcon size={14} color={colors.grey[700]} weight="regular" />
        <CustomText text="Undo" useTranslationText={false} size="xs" weight="medium" style={styles.ghostButtonText} />
      </Pressable>

      <View style={styles.divider} />

      <View style={styles.navGroup}>
        <Pressable
          accessibilityRole="button"
          accessibilityLabel="Navigate to previous item"
          onPress={onPrev}
          style={styles.kbdKey}>
          <ArrowLeftIcon size={12} color={colors.grey[700]} weight="bold" />
        </Pressable>
        <Pressable
          accessibilityRole="button"
          accessibilityLabel="Navigate to next item"
          onPress={onNext}
          style={styles.kbdKey}>
          <ArrowRightIcon size={12} color={colors.grey[700]} weight="bold" />
        </Pressable>
        <CustomText text="Navigate" useTranslationText={false} size="xs" style={styles.mutedText} />
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  bar: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingHorizontal: 20,
    height: 44,
    borderBottomWidth: 1,
    borderBottomColor: colors.grey[200],
    backgroundColor: colors.white,
    userSelect: "none",
  },
  leftGroup: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  rightGroup: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  acceptButton: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 6,
    backgroundColor: colors.brand[600],
    height: 26,
  },
  acceptButtonText: {
    color: colors.white,
  },
  skipButton: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 6,
    height: 26,
  },
  skipButtonText: {
    color: colors.grey[700],
  },
  badge: {
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 4,
    marginLeft: 4,
  },
  badgeSuccess: {
    backgroundColor: colors.success[50],
  },
  badgeError: {
    backgroundColor: colors.error[50],
  },
  badgeBrand: {
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 4,
    backgroundColor: colors.brand[50],
  },
  badgeSuccessText: {
    color: colors.success[700],
  },
  badgeErrorText: {
    color: colors.error[700],
  },
  badgeBrandText: {
    color: colors.brand[700],
  },
  ghostButton: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 6,
  },
  ghostButtonDisabled: {
    opacity: 0.5,
  },
  ghostButtonText: {
    color: colors.grey[700],
  },
  divider: {
    width: 1,
    height: 16,
    backgroundColor: colors.grey[200],
  },
  navGroup: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
  },
  kbdKey: {
    width: 24,
    height: 24,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: colors.white,
    borderWidth: 1,
    borderColor: colors.grey[200],
    borderRadius: 4,
  },
  mutedText: {
    color: colors.grey[500],
  },
});

export default ReviewActionPanel;
